/**
 * Google Analytics (gtag.js, G-BKGKCMMEBP loaded in index.html) event helper.
 * Funnel events: connect_wallet, open_game, buy_ticket, gift_tickets,
 * bet_coinflip, bet_sand, deposit_liquidity, withdraw_liquidity, swap,
 * share_ref_link. Best-effort: a blocked or missing gtag never disrupts the app.
 */
import { API_BASE } from './ton/config';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * GA4 user properties for traffic attribution: the referral key the visitor
 * arrived with (`referrer_key`, '0' = owner links, 'none' = organic) and the
 * partner SubID (`traffic_sub`) when present. Segments every event and
 * conversion of this user by the referrer who brought them, mirroring the
 * wallet_app segmentation. Applies to events fired after the call.
 *
 * GA4 keeps only the LATEST user-property value, and most returning users open
 * the Mini App without a start_param (bot menu button), so the key is remembered
 * in localStorage: a launch without a key reuses the last known one instead of
 * overwriting the whole user back to 'none'.
 */
const REF_LS = 'prmd.ga.ref';

export function gaSetReferrer(key: number | null, sub?: string | null): void {
  try {
    let val = key === null ? null : String(key);
    if (val === null) val = localStorage.getItem(REF_LS);
    else localStorage.setItem(REF_LS, val);
    window.gtag?.('set', 'user_properties', {
      referrer_key: val ?? 'none',
      ...(sub ? { traffic_sub: sub } : {}),
    });
  } catch {
    /* analytics must never break the app */
  }
}

/** Last known referrer key remembered by {@link gaSetReferrer}, or null. */
export function gaStoredReferrer(): string | null {
  try {
    return localStorage.getItem(REF_LS);
  } catch {
    return null;
  }
}

/**
 * The user's FIRST entry point into the ecosystem — 'bot', 'app', 'ai-bot',
 * 'partner-page' or 'site' — classified and frozen by the index.html boot
 * script on the very first visit (localStorage). 'unknown' when unavailable.
 */
export function gaEntryPoint(): string {
  try {
    return localStorage.getItem('prmd.ga.entry') ?? 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Finalizes the entry-point classification the index.html boot script left
 * unset for launch URLs without any tgWebApp* marker: with the Telegram SDK
 * loaded the context is unambiguous — a Mini App launch (start_param-less =
 * from the bot's button/menu) is 'bot', a plain browser visit is 'site'.
 * No-op when the entry point is already frozen.
 */
export function gaEnsureEntryPoint(inTelegram: boolean): void {
  try {
    if (localStorage.getItem('prmd.ga.entry')) return;
    const entry = inTelegram ? 'bot' : 'site';
    localStorage.setItem('prmd.ga.entry', entry);
    window.gtag?.('set', 'user_properties', { entry_point: entry });
  } catch {
    /* analytics must never break the app */
  }
}

export function gaEvent(name: string, params?: Record<string, string | number>): void {
  try {
    window.gtag?.('event', name, params ?? {});
  } catch {
    /* analytics must never break the app */
  }
}

const rateCache = new Map<string, { at: number; usd: number }>();

/** USD price of one whole token ('ton' for native GRAM, else the jetton
 *  master address), from our backend's /api/rates. 0 when unknown. */
async function usdRate(token: string): Promise<number> {
  const hit = rateCache.get(token);
  if (hit && Date.now() - hit.at < 300_000) return hit.usd;
  if (!API_BASE || !token) return 0;
  const r = await fetch(`${API_BASE}/api/rates?tokens=${encodeURIComponent(token)}`);
  const body = (await r.json()) as { rates?: Record<string, number> };
  const usd = Object.values(body.rates ?? {})[0] ?? 0;
  rateCache.set(token, { at: Date.now(), usd });
  return usd;
}

/**
 * Monetary funnel event: fires `name` with the token ticker, the token
 * amount and its USD estimate (`usd`, 2 decimals) so reports compare real
 * money across tokens. `token` is 'ton' for native GRAM or the jetton
 * master address; `ticker` is the display symbol. Fire-and-forget: a
 * missing rate sends usd 0 rather than delaying or dropping the event.
 */
export function gaMoneyEvent(
  name: string,
  ticker: string,
  amount: number,
  token: string = 'ton',
  extra?: Record<string, string | number>,
): void {
  usdRate(token)
    .catch(() => 0)
    .then((rate) => {
      gaEvent(name, {
        token: ticker,
        value: amount,
        usd: Math.round(amount * rate * 100) / 100,
        ...(extra ?? {}),
      });
    });
}
